import { ArrowBackIcon, ArrowForwardIcon } from "@chakra-ui/icons";
import { Button, Flex } from "@chakra-ui/react";
import useMediaQ from "@root/hooks/useMediaQ";
import { testId as testIdFn } from "@utils/e2e";
import { useTranslation } from "react-i18next";

export const Paginate = (props: PaginateProps) => {
  const {
    marginTop = 10,
    count,
    pageSize,
    page,
    onPageChange,
    size = "sm",
    selectedVariant = "outline",
    variant = "ghost",
    fontWeight = "medium",
    borderRadius = "md",
    isRtl = false,
    ...rest
  } = props;

  const { t } = useTranslation("common");
  const numberOfPages = Math.ceil(count / pageSize);
  const { md } = useMediaQ();

  const isPreviousDisabled = page <= 1;
  const isNextDisabled = page >= numberOfPages;

  const handlePageClick = (i: number) => {
    let newPage = i;
    if (i >= numberOfPages) newPage = numberOfPages;
    else if (i <= 0) newPage = 1;
    onPageChange(newPage);
  };

  // Generate page numbers with ellipsis
  const getPageNumbers = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    const maxVisible = md ? 5 : 3;

    if (numberOfPages <= maxVisible + 2) {
      for (let i = 1; i <= numberOfPages; i++) {
        pages.push(i);
      }
      return pages;
    }

    pages.push(1);

    let start = Math.max(2, page - 1);
    let end = Math.min(numberOfPages - 1, page + 1);

    if (page <= 3) {
      start = 2;
      end = Math.min(maxVisible, numberOfPages - 1);
    }

    if (page >= numberOfPages - 2) {
      start = Math.max(2, numberOfPages - maxVisible + 1);
      end = numberOfPages - 1;
    }

    if (start > 2) {
      pages.push("start-ellipsis");
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (end < numberOfPages - 1) {
      pages.push("end-ellipsis");
    }

    pages.push(numberOfPages);

    return pages;
  };

  const showPagination = numberOfPages > 1;

  if (!showPagination) return null;

  const pageNumbers = getPageNumbers();

  return (
    <Flex
      flexWrap="nowrap"
      justifyContent="space-between"
      alignItems="center"
      gap={2}
      mt={marginTop}
      width="100%"
      bgColor="transparent"
    >
      {/* Previous Button */}
      <Button
        {...rest}
        fontWeight={fontWeight}
        borderRadius={borderRadius}
        borderColor={isPreviousDisabled ? "#CDD6DC" : "#1819B4"}
        color={isPreviousDisabled ? "#CDD6DC" : "#1819B4"}
        size={size}
        variant="outline"
        isDisabled={isPreviousDisabled}
        onClick={(e) => {
          e.preventDefault();
          if (!isPreviousDisabled) {
            handlePageClick(page - 1);
          }
        }}
        leftIcon={<ArrowBackIcon boxSize={4} />}
        flexShrink={0}
        _disabled={{
          borderColor: "#CDD6DC",
          color: "#CDD6DC",
          cursor: "not-allowed",
          opacity: 0.6,
        }}
      >
        {t("page.previous")}
      </Button>

      {/* Page Numbers */}
      <Flex
        border="1px solid"
        m="0"
        borderColor="#FFFFFF"
        borderRadius="md"
        justifyContent="center"
        minW="90px"
      >
        {pageNumbers.map((item, index) => {
          if (item === "start-ellipsis" || item === "end-ellipsis") {
            return (
              <Button
                key={item}
                {...rest}
                fontWeight={fontWeight}
                borderRadius="full"
                size={size}
                variant="ghost"
                pointerEvents="none"
              >
                ...
              </Button>
            );
          }

          const pageNum = item as number;
          const isActive = pageNum === page;

          return (
            <Button
              key={pageNum}
              {...rest}
              fontWeight={fontWeight}
              borderRadius="1px"
              size={size}
              gap="0"
              variant={isActive ? "outline" : variant}
              colorScheme={isActive ? "#0073E01F" : "gray"}
              borderColor={isActive ? "blue.500" : "gray.200"}
              borderWidth={isActive ? "2px" : "1px"}
              color={isActive ? "#1819B4" : "#5C6670"}
              _hover={{ color: "blue.500" }}
              {...testIdFn(`pagination-page-${pageNum}`)}
              onClick={(e) => {
                e.preventDefault();
                handlePageClick(pageNum);
              }}
            >
              {pageNum}
            </Button>
          );
        })}
      </Flex>

      {/* Next Button */}
      <Button
        {...rest}
        fontWeight={fontWeight}
        borderRadius={borderRadius}
        color={isNextDisabled ? "#CDD6DC" : "#1819B4"}
        borderColor={isNextDisabled ? "#CDD6DC" : "#1819B4"}
        size={size}
        variant="outline"
        isDisabled={isNextDisabled}
        onClick={(e) => {
          e.preventDefault();
          if (!isNextDisabled) {
            handlePageClick(page + 1);
          }
        }}
        rightIcon={<ArrowForwardIcon boxSize={4} />}
        flexShrink={0}
        _disabled={{
          borderColor: "#CDD6DC",
          color: "#CDD6DC",
          cursor: "not-allowed",
          opacity: 0.6,
        }}
      >
        {t("page.next")}
      </Button>
    </Flex>
  );
};
